import { Buffer } from 'buffer';

import type { ApiSessionData } from '../api/types';
import type { DcId, SharedSessionData } from '../types';

import { ACCOUNT_SLOT, writeSlotSession } from './multiaccount';

type ImportedGramJsSessionPayload = {
  type: 'gramjs_session';
  apiId: number;
  apiHash: string;
  sessionString: string;
  phoneNumber?: string;
};

const IMPORT_QUERY_KEY = 'data';
const CURRENT_STRING_SESSION_VERSION = '1';

export function importLoginFromUrl() {
  if (typeof window === 'undefined') {
    return false;
  }

  const url = new URL(window.location.href);
  const encodedData = getImportedLoginData(url);
  if (!encodedData) {
    return false;
  }

  clearImportedLoginQuery(url);

  try {
    const payload = parseImportedPayload(encodedData);
    const sessionData = parseStringSession(payload.sessionString);

    const nextSlotData: SharedSessionData = {
      date: Date.now(),
      dcId: sessionData.mainDcId,
      apiId: payload.apiId,
      apiHash: payload.apiHash,
      phone: payload.phoneNumber,
    };

    Object.entries(sessionData.keys).forEach(([dcId, authKey]) => {
      nextSlotData[`dc${Number(dcId) as DcId}_auth_key`] = authKey;
    });

    writeSlotSession(ACCOUNT_SLOT, nextSlotData);
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to import login data from URL', err);
    return false;
  }
}

function clearImportedLoginQuery(url: URL) {
  url.searchParams.delete(IMPORT_QUERY_KEY);

  const [hashPath, hashQuery = ''] = url.hash.split('?');
  if (hashQuery) {
    const hashParams = new URLSearchParams(hashQuery);
    hashParams.delete(IMPORT_QUERY_KEY);
    const nextHashQuery = hashParams.toString();
    url.hash = nextHashQuery ? `${hashPath}?${nextHashQuery}` : hashPath;
  }

  window.history.replaceState({}, document.title, url.toString());
}

function getImportedLoginData(url: URL) {
  const searchValue = url.searchParams.get(IMPORT_QUERY_KEY);
  if (searchValue) {
    return searchValue;
  }

  const [, hashQuery = ''] = url.hash.split('?');
  if (!hashQuery) {
    return undefined;
  }

  return new URLSearchParams(hashQuery).get(IMPORT_QUERY_KEY);
}

function parseImportedPayload(encodedData: string): ImportedGramJsSessionPayload {
  let parsed: ImportedGramJsSessionPayload;

  try {
    parsed = JSON.parse(decodeBase64Url(encodedData)) as ImportedGramJsSessionPayload;
  } catch (err) {
    throw new Error('Invalid import payload');
  }

  if (parsed.type !== 'gramjs_session') {
    throw new Error('Unsupported import payload type');
  }

  if (!parsed.sessionString || !parsed.apiHash || !parsed.apiId || !Number.isFinite(Number(parsed.apiId))) {
    throw new Error('Incomplete import payload');
  }

  return {
    ...parsed,
    apiId: Number(parsed.apiId),
  };
}

function parseStringSession(sessionString: string): ApiSessionData {
  if (!sessionString || sessionString[0] !== CURRENT_STRING_SESSION_VERSION) {
    throw new Error('Invalid string session');
  }

  const encodedSession = sessionString.slice(1);
  const buffer = Buffer.from(base64UrlToBase64(encodedSession), 'base64');

  let offset = 0;
  const mainDcId = buffer.readUInt8(offset);
  offset += 1;

  if (encodedSession.length === 352) {
    offset += 4;
  } else {
    const serverAddressLen = buffer.readInt16BE(offset);
    offset += 2;
    offset += serverAddressLen > 100 ? 16 : serverAddressLen;
  }

  offset += 2;

  const authKey = buffer.subarray(offset).toString('hex');
  if (!authKey) {
    throw new Error('Missing auth key in string session');
  }

  return {
    mainDcId,
    keys: {
      [mainDcId]: authKey,
    },
  };
}

function decodeBase64Url(value: string) {
  return Buffer.from(base64UrlToBase64(value), 'base64').toString('utf8');
}

function base64UrlToBase64(value: string) {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
  return normalized + padding;
}
